import React, { useEffect, useState } from "react";
import { RequestMethod, askAPI } from "../api/api";
import { imgFromCoords } from "../utils/settings";

export interface AdminSettingsDto {
  hexPromptTemplate: string;
  hexPromptTemplateAdmin: string;
  hexNegativePrompt: string;
  hexNegativePromptAdmin: string;
  llmInstruction: string | null;
  videoPromptTemplate: string;
  videoEnhancePromptForFal: boolean;
  devProxyCdnImages: boolean;
}

export interface VideoGenerationsByMonth {
  year: number;
  month: number;
  totalGenerations: number;
  uniqueUsers: number;
  avgPerUser: number;
}

export interface RecentPayment {
  id: string;
  playerUid: string | null;
  amountTotal: number;
  currency: string;
  created: number;
}

export interface AdminStats {
  totalUsers: number;
  avgGenerationsPerUserDay: number;
  avgGenerationsPerUserWeek: number;
  avgGenerationsPerUserMonth: number;
  avgPoints: number;
  avgUpvotes: number;
  lastGenerations: { x: number; y: number }[];
  videoGenerationsByMonth: VideoGenerationsByMonth[];
  recentPayments?: RecentPayment[];
}

export const AdminPage: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<{ x: number; y: number } | null>(null);
  const [settings, setSettings] = useState<AdminSettingsDto | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [geoHeaders, setGeoHeaders] = useState<Record<string, string> | null>(null);

  const fetchStats = async () => {
    setLoading(true);
    const data = await askAPI<AdminStats>("/api/admin/stats", RequestMethod.GET);
    setStats(data || null);
    setLoading(false);
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    const data = await askAPI<AdminSettingsDto>("/api/admin/settings", RequestMethod.GET);
    setSettings(data || null);
    setSettingsLoading(false);
  };

  const fetchGeoHeaders = async () => {
    const data = await askAPI<Record<string, string>>("/api/admin/debug-geo", RequestMethod.GET);
    setGeoHeaders(data || null);
  };

  useEffect(() => {
    fetchStats();
    fetchSettings();
    fetchGeoHeaders();
  }, []);

  const handleSaveSettings = async () => {
    if (!settings) return;
    setSettingsSaving(true);
    const payload: Partial<AdminSettingsDto> = {
      hexPromptTemplate: settings.hexPromptTemplate,
      hexPromptTemplateAdmin: settings.hexPromptTemplateAdmin ?? "",
      hexNegativePrompt: settings.hexNegativePrompt,
      hexNegativePromptAdmin: settings.hexNegativePromptAdmin ?? "",
      llmInstruction: settings.llmInstruction || null,
      videoPromptTemplate: settings.videoPromptTemplate,
      videoEnhancePromptForFal: settings.videoEnhancePromptForFal,
      devProxyCdnImages: settings.devProxyCdnImages,
    };
    const updated = await askAPI<AdminSettingsDto>("/api/admin/settings", RequestMethod.PATCH, payload);
    if (updated) setSettings(updated);
    setSettingsSaving(false);
  };

  const handleDelete = async (x: number, y: number) => {
    if (!window.confirm(`Delete hex at (${x}, ${y})?`)) return;
    setDeleting({ x, y });
    await askAPI("/api/hexagen/delete", RequestMethod.POST, { x, y });
    setDeleting(null);
    if (stats) {
      setStats({
        ...stats,
        lastGenerations: stats.lastGenerations.filter(
          (h) => !(h.x === x && h.y === y)
        ),
      });
    }
  };

  if (loading) {
    return (
      <div className="h-screen overflow-y-auto flex items-center justify-center bg-black text-white">
        Loading…
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="h-screen overflow-y-auto flex items-center justify-center bg-black text-white">
        Failed to load stats
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto overflow-x-hidden bg-black text-white p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Admin</h1>

        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-gray-800 rounded p-3">
              <div className="text-gray-400 text-sm">Total users</div>
              <div className="text-xl">{stats.totalUsers}</div>
            </div>
            <div className="bg-gray-800 rounded p-3">
              <div className="text-gray-400 text-sm">Avg generations / user (day)</div>
              <div className="text-xl">{stats.avgGenerationsPerUserDay}</div>
            </div>
            <div className="bg-gray-800 rounded p-3">
              <div className="text-gray-400 text-sm">Avg generations / user (week)</div>
              <div className="text-xl">{stats.avgGenerationsPerUserWeek}</div>
            </div>
            <div className="bg-gray-800 rounded p-3">
              <div className="text-gray-400 text-sm">Avg generations / user (month)</div>
              <div className="text-xl">{stats.avgGenerationsPerUserMonth}</div>
            </div>
            <div className="bg-gray-800 rounded p-3">
              <div className="text-gray-400 text-sm">Avg points</div>
              <div className="text-xl">{stats.avgPoints}</div>
            </div>
            <div className="bg-gray-800 rounded p-3">
              <div className="text-gray-400 text-sm">Avg upvotes per hex</div>
              <div className="text-xl">{stats.avgUpvotes}</div>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Recent payments (500 Sparks)</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left border border-gray-700 rounded overflow-hidden">
              <thead>
                <tr className="bg-gray-800">
                  <th className="p-2 border-b border-gray-600">Date</th>
                  <th className="p-2 border-b border-gray-600">Player UID</th>
                  <th className="p-2 border-b border-gray-600">Amount</th>
                  <th className="p-2 border-b border-gray-600">Session ID</th>
                </tr>
              </thead>
              <tbody>
                {(stats.recentPayments || []).map((p) => (
                  <tr key={p.id} className="border-b border-gray-700">
                    <td className="p-2 text-sm">
                      {new Date(p.created * 1000).toISOString().replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="p-2 font-mono text-xs">{p.playerUid ?? "—"}</td>
                    <td className="p-2">
                      {(p.amountTotal / 100).toFixed(2)} {p.currency}
                    </td>
                    <td className="p-2 font-mono text-xs truncate max-w-[180px]" title={p.id}>
                      {p.id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!stats.recentPayments || stats.recentPayments.length === 0) && (
            <p className="text-gray-400 text-sm mt-2">No recent payments.</p>
          )}
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Video generations per month</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left border border-gray-700 rounded overflow-hidden">
              <thead>
                <tr className="bg-gray-800">
                  <th className="p-2 border-b border-gray-600">Month</th>
                  <th className="p-2 border-b border-gray-600">Total</th>
                  <th className="p-2 border-b border-gray-600">Unique users</th>
                  <th className="p-2 border-b border-gray-600">Avg per user</th>
                </tr>
              </thead>
              <tbody>
                {(stats.videoGenerationsByMonth || []).map((row) => (
                  <tr key={`${row.year}-${row.month}`} className="border-b border-gray-700">
                    <td className="p-2">{`${row.year}-${String(row.month).padStart(2, "0")}`}</td>
                    <td className="p-2">{row.totalGenerations}</td>
                    <td className="p-2">{row.uniqueUsers}</td>
                    <td className="p-2">{row.avgPerUser}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!stats.videoGenerationsByMonth || stats.videoGenerationsByMonth.length === 0) && (
            <p className="text-gray-400 text-sm mt-2">No video generations in the last 12 months.</p>
          )}
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Last 64 generations</h2>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {stats.lastGenerations.map(({ x, y }) => (
              <div
                key={`${x}-${y}`}
                className="bg-gray-800 rounded overflow-hidden group relative"
              >
                <a href={`/hex/${x}/${y}`} target="_blank" rel="noopener noreferrer">
                  <img
                    src={imgFromCoords(x, y)}
                    alt={`${x},${y}`}
                    className="w-full aspect-square object-cover block"
                  />
                </a>
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a
                    href={`/hex/${x}/${y}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-white mr-1 underline"
                  >
                    Map
                  </a>
                  <button
                    type="button"
                    onClick={() => handleDelete(x, y)}
                    disabled={!!deleting}
                    className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                  >
                    {deleting?.x === x && deleting?.y === y ? "…" : "Delete"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-4">Generation prompts (hex & video)</h2>
          {settingsLoading ? (
            <p className="text-gray-400">Loading settings…</p>
          ) : settings ? (
            <div className="space-y-4 max-w-3xl">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Stable Diffusion: hex prompt template (use {"{{prompt}}"} for LLM output)</label>
                <textarea
                  value={settings.hexPromptTemplate}
                  onChange={(e) => setSettings({ ...settings, hexPromptTemplate: e.target.value })}
                  rows={6}
                  className="w-full bg-gray-800 text-white rounded p-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Stable Diffusion: hex negative prompt</label>
                <textarea
                  value={settings.hexNegativePrompt}
                  onChange={(e) => setSettings({ ...settings, hexNegativePrompt: e.target.value })}
                  rows={6}
                  className="w-full bg-gray-800 text-white rounded p-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Admin-only: hex prompt template (overrides default for admin users when non-empty; use {"{{prompt}}"} for LLM output)</label>
                <textarea
                  value={settings.hexPromptTemplateAdmin ?? ""}
                  onChange={(e) => setSettings({ ...settings, hexPromptTemplateAdmin: e.target.value })}
                  rows={4}
                  className="w-full bg-gray-800 text-white rounded p-2 text-sm font-mono"
                  placeholder="Leave empty to use the default template above"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Admin-only: hex negative prompt (overrides default for admin users when non-empty)</label>
                <textarea
                  value={settings.hexNegativePromptAdmin ?? ""}
                  onChange={(e) => setSettings({ ...settings, hexNegativePromptAdmin: e.target.value })}
                  rows={3}
                  className="w-full bg-gray-800 text-white rounded p-2 text-sm font-mono"
                  placeholder="Leave empty to use the default negative prompt below"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Hex generation: LLM system instruction (empty = use default)</label>
                <textarea
                  value={settings.llmInstruction ?? ""}
                  onChange={(e) => setSettings({ ...settings, llmInstruction: e.target.value || null })}
                  rows={6}
                  className="w-full bg-gray-800 text-white rounded p-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Video prompt template (FAL)</label>
                <textarea
                  value={settings.videoPromptTemplate}
                  onChange={(e) => setSettings({ ...settings, videoPromptTemplate: e.target.value })}
                  rows={6}
                  className="w-full bg-gray-800 text-white rounded p-2 text-sm font-mono"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="videoEnhance"
                  checked={settings.videoEnhancePromptForFal}
                  onChange={(e) => setSettings({ ...settings, videoEnhancePromptForFal: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="videoEnhance" className="text-sm">
                  Enhance video prompt for FAL (prepend hex LLM prompt to video template)
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="devProxyCdn"
                  checked={settings.devProxyCdnImages ?? false}
                  onChange={(e) => setSettings({ ...settings, devProxyCdnImages: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="devProxyCdn" className="text-sm">
                  Dev server: proxy missing images from real CDN
                </label>
              </div>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={settingsSaving}
                  className="bg-white text-black hover:bg-gray-200 disabled:opacity-50 disabled:hover:bg-white px-5 py-0 rounded font-medium text-sm transition-colors"
                >
                  {settingsSaving ? "Saving…" : "Save prompts"}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-gray-400">Failed to load settings.</p>
          )}
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Debug: GEO_ / CF headers</h2>
          <p className="text-gray-400 text-sm mb-2">
            Headers from the request that loaded this page (from CF / proxy).
          </p>
          <div className="bg-gray-800 rounded p-3 font-mono text-sm overflow-x-auto">
            {geoHeaders === null ? (
              <span className="text-gray-500">Loading…</span>
            ) : Object.keys(geoHeaders).length === 0 ? (
              <span className="text-gray-500">No geo/cf headers found.</span>
            ) : (
              <ul className="list-none space-y-1">
                {Object.entries(geoHeaders)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, value]) => (
                    <li key={key}>
                      <span className="text-amber-400">{key}</span>:{" "}
                      <span className="text-gray-300">{value}</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={fetchGeoHeaders}
            className="mt-2 text-sm text-gray-400 hover:text-white underline"
          >
            Refresh
          </button>
        </section>
      </div>
    </div>
  );
};
